import { documentToHtmlString } from '@contentful/rich-text-html-renderer'

const createSchemaObject = (type, obj) => ({
  '@type': type,
  ...obj,
})

/**
 * Generates an array of stringified JSON from an array of objects with SEO data from Contentful.
 * An example use for this functions output is data for React Helmet to add json+ld to the <head>
 * tag of each webpage.
 * NOTE: This function does not return the whole array stringified because it needs to be mapped over
 * to generate each json object inside a script tag for current usage.
 *
 * @param {object} contentfulSEOData - SEO Page data provided from the Contentful Space
 * @property {string} contentfulSEOData.title - Page Title
 * @property {string} contentfulSEOData.description - Page Descriptioon
 * @property {string} [contentfulSEOData.schemaType = "WebPage"] - Page type (e.g. AboutPage, FAQPage)
 * @property {object} [contentfulSEOData.mainEntity] - Reference to a Contentful Entry
 * @property {object} [contentfulSEOData.customSchema] - Data from Contentful JSON field for any handwritten schema.
 * @property {boolean} [contentfulSEOData.override] - If true, customSchema overrides automated schema.
 *                                                    If false, customSchema is appended to automated schema.
 *
 * @param {object[]} [startingSchema] - Initial schema objects to extend final result from
 *
 * @returns {string[]} - Array of stringified JSON to be written into script tags
 */
const buildSeoSchema = (
  { title, description, schemaType, mainEntity, customSchema, override },
  startingSchema = []
) => {
  const schemaObjects = [...startingSchema]

  const appendCustomSchema = () => {
    if (customSchema && customSchema.length > 0) {
      customSchema.forEach((customSchemaObj) => {
        const customSchemaContent = customSchemaObj?.internal?.content || ''
        schemaObjects.push(JSON.parse(customSchemaContent))
      })
    }
  }

  /*
   *  If the override option is set/true, add the custom schema to
   * the starting schema. This does not override the starting schema,
   * but it does override the rest of the page schema that comes from,
   * contentful.
   */
  if (override) {
    appendCustomSchema()
    return schemaObjects.map((schemaObj) => JSON.stringify(schemaObj, null, 2))
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    ...createSchemaObject(schemaType || 'WebPage', {
      name: title,
      description,
    }),
  }

  let entitySchema

  if (mainEntity) {
    const { __typename: entityType } = mainEntity
    if (entityType === 'ContentfulPackage') {
      const { brandy } = mainEntity

      entitySchema = createSchemaObject('Product', {
        name: brandy.name,
        offers: createSchemaObject('Offer', {
          priceCurrency: 'USD',
          price: brandy.price.amount,
        }),
      })
    }

    if (entityType === 'ContentfulCollection') {
      const { identifier, items } = mainEntity

      switch (schemaType) {
        case 'FAQPage':
          entitySchema = items.map((item) =>
            createSchemaObject('Question', {
              name: item.heading,
              acceptedAnswer: createSchemaObject('Answer', {
                text:
                  item?.mainContent?.raw &&
                  documentToHtmlString(JSON.parse(item.mainContent.raw)),
              }),
            })
          )
          break
        default:
          entitySchema = createSchemaObject('ItemList', {
            name: identifier,
            itemListElement: items.map((item, index) =>
              createSchemaObject('ListItem', {
                position: index + 1,
                item: createSchemaObject('Thing', {
                  name: item.heading,
                }),
              })
            ),
          })
      }
    }
  }

  schemaObjects.push({
    ...pageSchema,
    mainEntity: entitySchema,
  })

  appendCustomSchema()

  return schemaObjects.map((schemaObj) => JSON.stringify(schemaObj, null, 2))
}

export default buildSeoSchema
